import { ReactNode, useEffect, useRef, useState } from "react";
import { Platform, View } from "react-native";

type WebDropZoneProps = Readonly<{
  onDropFiles: (files: File[]) => void;
  onDropRejected?: () => void;
  children?: ReactNode;
  className?: string;
  disabled?: boolean;
  acceptedMimeTypes?: string[];
  renderChildren?: (isDragActive: boolean) => ReactNode;
}>;

export default function WebDropZone({
  onDropFiles,
  onDropRejected,
  children,
  className,
  disabled = false,
  acceptedMimeTypes,
  renderChildren,
}: WebDropZoneProps) {
  const ref = useRef<View | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);

  useEffect(() => {
    if (Platform.OS !== "web" || disabled) return;

    const node = ref.current as unknown as HTMLElement | null;
    if (!node) return;

    const prevent = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDragEnter = (e: Event) => {
      prevent(e);
      setIsDragActive(true);
    };
    const handleDragOver = (e: Event) => {
      prevent(e);
      setIsDragActive(true);
    };
    const handleDragLeave = (e: Event) => {
      prevent(e);
      setIsDragActive(false);
    };

    const handleDrop = (e: Event) => {
      prevent(e);
      setIsDragActive(false);
      const dropped = (e as DragEvent).dataTransfer?.files;
      if (!dropped || dropped.length === 0) return;

      const files = Array.from(dropped);

      if (acceptedMimeTypes && acceptedMimeTypes.length > 0) {
        const accepted = files.filter((f) =>
          acceptedMimeTypes.includes(f.type),
        );
        if (accepted.length === 0) {
          onDropRejected?.();
          return;
        }
        onDropFiles(accepted);
      } else {
        onDropFiles(files);
      }
    };

    node.addEventListener("dragenter", handleDragEnter);
    node.addEventListener("dragover", handleDragOver);
    node.addEventListener("dragleave", handleDragLeave);
    node.addEventListener("drop", handleDrop);

    return () => {
      node.removeEventListener("dragenter", handleDragEnter);
      node.removeEventListener("dragover", handleDragOver);
      node.removeEventListener("dragleave", handleDragLeave);
      node.removeEventListener("drop", handleDrop);
    };
  }, [onDropFiles, onDropRejected, disabled, acceptedMimeTypes]);

  return (
    <View ref={ref} className={className}>
      {renderChildren ? renderChildren(isDragActive) : children}
    </View>
  );
}
